import { DialogActions } from '@mui/material'
import { styled } from '@mui/system'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import TableBody from '@mui/material/TableBody'
import Paper from '@mui/material/Paper'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import {
  InputCell,
  RowHeaderCell,
  StyledTableRow,
} from '../../containers/DataCell'
import TextInput from '../../components/editors/input/TextInput'
import Auth, { PasswordPolicy } from '../../../lib/commons/auth'
import { FontSize } from '../../../styles/commonStyles'
import {
  WhitespaceNotice,
  applyPasswordInputEvent,
  isWhitespaceKey,
  whitespaceNoticeViewOf,
} from '../../../utils/password'
import { NoticeText } from '../../components/labels/NoticeText'

// Styles
const StyledDialogContent = styled(DialogContent)({
  width: '410px',
})
const Title = styled('div')({
  fontWeight: 'bold',
  marginBottom: '10px',
  fontSize: FontSize.LARGE,
})
const StyledTable = styled(Table)({
  width: '100%',
  height: '100%',
})

interface Props extends WrappedComponentProps {
  open: boolean
  target: string
  onSubmit: Function
  onClose: Function
}

interface State {
  password: string
  passwordPolicy?: PasswordPolicy
  whitespaceNotice: WhitespaceNotice
}

const DEFAULT_PWD_MIN_LENGTH = 6

class ResetUserPasswordDialog extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      password: '',
      whitespaceNotice: undefined,
    }
  }

  async componentDidMount() {
    const tenant = Auth.getCurrentTenant()
    if (tenant) {
      const passwordPolicy = await tenant.getPasswordPolicy()
      const tempPwd = await tenant.issueTemporaryPassword(
        passwordPolicy?.minLength || DEFAULT_PWD_MIN_LENGTH
      )
      this.setState({
        passwordPolicy,
        password: tempPwd,
      })
    }
  }

  private submit = () => {
    this.props.onSubmit(this.state.password)
    this.close()
  }

  private getDisabledReason = (): string | undefined => {
    const { intl } = this.props
    const { password, passwordPolicy } = this.state
    if (!password) {
      return intl.formatMessage({ id: 'user.resetPassword.error.empty' })
    }
    const minLength = passwordPolicy?.minLength || DEFAULT_PWD_MIN_LENGTH
    if (password.length < minLength) {
      return intl.formatMessage(
        { id: 'user.resetPassword.error.tooShort' },
        { min: minLength }
      )
    }
    return undefined
  }

  private onChangePassword = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    this.setState(prev => {
      const result = applyPasswordInputEvent(event, prev.password)
      return { password: result.value, whitespaceNotice: result.notice }
    })
  }

  private onKeyDownPassword = (event: React.KeyboardEvent<HTMLElement>) => {
    if (isWhitespaceKey(event.key)) {
      event.preventDefault()
      this.setState({ whitespaceNotice: 'notAllowed' })
    }
  }

  private close = () => {
    this.setState({ password: '', whitespaceNotice: undefined })
    this.props.onClose()
  }

  render() {
    const { open, target, intl } = this.props
    const disabledReason = this.getDisabledReason()

    return (
      <Dialog
        open={open}
        onClose={this.close}
        PaperProps={{ style: { overflow: 'visible' } }}
      >
        <StyledDialogContent>
          <Title>
            {intl.formatMessage(
              { id: 'user.resetPassword.confirmation' },
              { target }
            )}
          </Title>
          <div>
            <TableContainer component={Paper}>
              <StyledTable size="small" aria-label="customized table">
                <TableBody>
                  <StyledTableRow key="temporaryPassword">
                    <RowHeaderCell align="left">
                      {intl.formatMessage({
                        id: 'user.resetPassword.temporaryPassword',
                      })}
                    </RowHeaderCell>
                    <InputCell align="left" colSpan={4}>
                      <TextInput
                        fullWidth={true}
                        variant="standard"
                        value={this.state.password}
                        onChange={this.onChangePassword}
                        onKeyDown={this.onKeyDownPassword}
                        InputProps={{ disableUnderline: true }}
                        placeholder={intl.formatMessage(
                          { id: 'user.resetPassword.placeholder' },
                          {
                            min:
                              this.state.passwordPolicy?.minLength ||
                              DEFAULT_PWD_MIN_LENGTH,
                          }
                        )}
                        helperText={
                          <NoticeText
                            {...whitespaceNoticeViewOf(
                              this.state.whitespaceNotice
                            )}
                          />
                        }
                      />
                    </InputCell>
                  </StyledTableRow>
                </TableBody>
              </StyledTable>
            </TableContainer>
          </div>
        </StyledDialogContent>
        <DialogActions>
          <Button onClick={this.close}>
            {intl.formatMessage({ id: 'dialog.close' })}
          </Button>
          <Tooltip title={disabledReason || ''}>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={this.submit}
                disabled={!!disabledReason}
              >
                {intl.formatMessage({ id: 'dialog.setting' })}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    )
  }
}

export default injectIntl(ResetUserPasswordDialog)
